import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ExperienceCard from '../components/ExperienceCard';
import Footer from '../components/Footer';
import { Map, List, SlidersHorizontal, Loader2, Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { useCurrency } from '../context/CurrencyContext';
import axios from 'axios';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const cuisineTypes = [
  'Italian', 'Japanese', 'Mexican', 'French', 'Indian', 'Thai', 'Chinese',
  'Mediterranean', 'American', 'Korean', 'Vietnamese', 'Greek', 'Spanish',
  'Middle Eastern', 'Ethiopian', 'Peruvian', 'Caribbean', 'Fusion'
];

// Helper functions for dates
const formatDate = (date) => {
  if (!date) return '';
  return date.toISOString().split('T')[0];
};

const formatDisplayDate = (date) => {
  if (!date) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getMonthName = (month, year) => {
  return new Date(year, month).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

const getDaysInMonth = (month, year) => {
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (month, year) => {
  return new Date(year, month, 1).getDay();
};

// Date Picker Component
const DateRangePicker = ({ startDate, endDate, selectedMonth, onDateChange, onMonthSelect, onClear }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + 90);
  
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [selectingEnd, setSelectingEnd] = useState(false);
  
  const canGoPrev = viewMonth > today.getMonth() || viewYear > today.getFullYear();
  const canGoNext = new Date(viewYear, viewMonth + 1, 1) <= maxDate;
  
  const goToPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };
  
  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };
  
  const handleDayClick = (day) => {
    const clickedDate = new Date(viewYear, viewMonth, day);
    clickedDate.setHours(0, 0, 0, 0);
    
    if (clickedDate < today || clickedDate > maxDate) return;
    
    if (!selectingEnd || !startDate) {
      // Selecting start date
      onDateChange(clickedDate, null);
      setSelectingEnd(true);
    } else {
      // Selecting end date
      if (clickedDate >= startDate) {
        onDateChange(startDate, clickedDate);
      } else {
        onDateChange(clickedDate, startDate);
      }
      setSelectingEnd(false);
    }
  };
  
  const handleMonthClick = () => {
    const monthStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
    onMonthSelect(monthStr);
    setSelectingEnd(false);
  };
  
  const isDateInRange = (day) => {
    if (!startDate || !endDate) return false;
    const date = new Date(viewYear, viewMonth, day);
    return date >= startDate && date <= endDate;
  };
  
  const isStartDate = (day) => {
    if (!startDate) return false;
    const date = new Date(viewYear, viewMonth, day);
    return date.getTime() === startDate.getTime();
  };
  
  const isEndDate = (day) => {
    if (!endDate) return false;
    const date = new Date(viewYear, viewMonth, day);
    return date.getTime() === endDate.getTime();
  };
  
  const isDisabled = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    return date < today || date > maxDate;
  };
  
  const isToday = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    return date.getTime() === today.getTime();
  };
  
  const daysInMonth = getDaysInMonth(viewMonth, viewYear);
  const firstDay = getFirstDayOfMonth(viewMonth, viewYear);
  
  // Adjust firstDay so Monday = 0
  const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;
  
  const days = [];
  for (let i = 0; i < adjustedFirstDay; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }
  
  // Check if current view month is selected
  const currentMonthStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
  const isMonthSelected = selectedMonth === currentMonthStr;
  
  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={goToPrevMonth}
          disabled={!canGoPrev}
          className={`p-1 rounded-full hover:bg-gray-100 ${!canGoPrev ? 'opacity-30 cursor-not-allowed' : ''}`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="text-center">
          <h3 className="font-semibold">{getMonthName(viewMonth, viewYear)}</h3>
          <button
            onClick={handleMonthClick}
            className={`text-xs px-2 py-1 rounded-full transition-colors ${
              isMonthSelected
                ? 'bg-terracotta text-white'
                : 'text-terracotta hover:bg-terracotta/10'
            }`}
          >
            {isMonthSelected ? 'Month selected' : 'Select entire month'}
          </button>
        </div>
        <button
          onClick={goToNextMonth}
          disabled={!canGoNext}
          className={`p-1 rounded-full hover:bg-gray-100 ${!canGoNext ? 'opacity-30 cursor-not-allowed' : ''}`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
      
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day) => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, index) => (
          <div key={day ? day.toISOString() : `empty-${index}`} className="aspect-square">
            {day && (
              <button
                onClick={() => handleDayClick(day)}
                disabled={isDisabled(day)}
                className={`w-full h-full flex items-center justify-center text-sm rounded-full transition-colors
                  ${isDisabled(day) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}
                  ${isStartDate(day) || isEndDate(day) ? 'bg-terracotta text-white hover:bg-terracotta' : ''}
                  ${isDateInRange(day) && !isStartDate(day) && !isEndDate(day) ? 'bg-terracotta/20' : ''}
                  ${isToday(day) && !isStartDate(day) && !isEndDate(day) ? 'border-2 border-terracotta' : ''}
                `}
              >
                {day}
              </button>
            )}
          </div>
        ))}
      </div>
      
      {/* Footer */}
      <div className="mt-4 pt-4 border-t">
        <div className="text-xs text-gray-500 mb-2">
          {selectingEnd && startDate ? 'Select end date' : 'Select start date'}
        </div>
        {(startDate || endDate || selectedMonth) && (
          <div className="flex items-center justify-between">
            <div className="text-sm">
              {selectedMonth ? (
                <span className="font-medium">{getMonthName(parseInt(selectedMonth.split('-')[1]) - 1, parseInt(selectedMonth.split('-')[0]))}</span>
              ) : (
                <>
                  <span className="font-medium">{formatDisplayDate(startDate)}</span>
                  {endDate && (
                    <>
                      <span className="mx-1">→</span>
                      <span className="font-medium">{formatDisplayDate(endDate)}</span>
                    </>
                  )}
                </>
              )}
            </div>
            <button
              onClick={onClear}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          </div>
        )}
        <p className="text-xs text-gray-400 mt-2">
          Search up to 90 days from today
        </p>
      </div>
    </div>
  );
};

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const { formatPrice } = useCurrency();
  const [showMap, setShowMap] = useState(true);
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [guestFilter, setGuestFilter] = useState(0);
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Date filtering state
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);

  const location = searchParams.get('location') || '';
  const guests = parseInt(searchParams.get('guests') || '1');

  // Fetch experiences when filters change
  useEffect(() => {
    fetchExperiences();
  }, [location, startDate, endDate, selectedMonth]);

  const fetchExperiences = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (location) {
        params.append('location', location);
      }
      if (selectedMonth) {
        params.append('month', selectedMonth);
      } else if (startDate && endDate) {
        params.append('start_date', formatDate(startDate));
        params.append('end_date', formatDate(endDate));
      } else if (startDate) {
        params.append('start_date', formatDate(startDate));
        params.append('end_date', formatDate(startDate));
      }
      
      const response = await axios.get(`${API_URL}/api/experiences?${params.toString()}`);
      setExperiences(response.data);
    } catch (error) {
      console.error('Failed to fetch experiences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setSelectedMonth(null);
  };
  
  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    setStartDate(null);
    setEndDate(null);
    setDatePopoverOpen(false);
  };
  
  const clearDateFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedMonth(null);
  };

  // Date filter display text
  const dateFilterText = useMemo(() => {
    if (selectedMonth) {
      const [year, month] = selectedMonth.split('-');
      return getMonthName(parseInt(month) - 1, parseInt(year));
    }
    if (startDate && endDate) {
      return `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;
    }
    if (startDate) {
      return formatDisplayDate(startDate);
    }
    return 'Any dates';
  }, [startDate, endDate, selectedMonth]);

  const hasDateFilter = startDate || endDate || selectedMonth;

  // Client-side filtering
  const filteredExperiences = experiences.filter(exp => {
    // Guests filter
    if (exp.max_guests < guests) {
      return false;
    }
    // Price filter
    if (exp.price < priceRange[0] || exp.price > priceRange[1]) {
      return false;
    }
    // Guest capacity filter
    if (guestFilter > 0 && exp.max_guests < guestFilter * 5) {
      return false;
    }
    // Cuisine filter
    if (selectedCuisines.length > 0 && !selectedCuisines.includes(exp.cuisine)) {
      return false;
    }
    return true;
  });

  const toggleCuisine = (cuisine) => {
    setSelectedCuisines(prev =>
      prev.includes(cuisine)
        ? prev.filter(c => c !== cuisine)
        : [...prev, cuisine]
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F4EDE4]" data-testid="search-page">
      <Navbar />
      <div className="h-20" />

      {/* Filter Bar */}
      <div className="border-b bg-white sticky top-20 z-30">
        <div className="max-w-[2000px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Date Filter */}
            <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`flex items-center gap-2 ${hasDateFilter ? 'border-terracotta text-terracotta' : ''}`}
                  data-testid="date-filter-btn"
                >
                  <Calendar className="w-4 h-4" />
                  <span className="max-w-[150px] truncate">{dateFilterText}</span>
                  {hasDateFilter && (
                    <X
                      className="w-4 h-4 ml-1 hover:text-red-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        clearDateFilter();
                      }}
                    />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[320px] p-0" align="start">
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  selectedMonth={selectedMonth}
                  onDateChange={handleDateChange}
                  onMonthSelect={handleMonthSelect}
                  onClear={clearDateFilter}
                />
              </PopoverContent>
            </Popover>

            {/* Other Filters */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2" data-testid="filters-btn">
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[400px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="py-6 space-y-8">
                  {/* Price Range */}
                  <div>
                    <h3 className="font-semibold mb-4">Price range</h3>
                    <Slider
                      value={priceRange}
                      onValueChange={setPriceRange}
                      max={500}
                      step={10}
                      className="mb-4"
                      data-testid="price-slider"
                    />
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{formatPrice(priceRange[0])}</span>
                      <span>{formatPrice(priceRange[1])}+</span>
                    </div>
                  </div>

                  {/* Group Size */}
                  <div>
                    <h3 className="font-semibold mb-4">Group size</h3>
                    <div className="grid grid-cols-4 gap-2">
                      {[
                        { label: 'Any', value: 0 },
                        { label: '1-5', value: 1 },
                        { label: '6-10', value: 2 },
                        { label: '10+', value: 3 },
                      ].map((option) => (
                        <Button
                          key={option.value}
                          variant={guestFilter === option.value ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setGuestFilter(option.value)}
                          className={guestFilter === option.value ? 'bg-[#D65A31]' : ''}
                          data-testid={`group-size-${option.value}`}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Cuisine Type */}
                  <div>
                    <h3 className="font-semibold mb-4">Cuisine type</h3>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {cuisineTypes.map((cuisine) => (
                        <label
                          key={cuisine}
                          className="flex items-center gap-3 cursor-pointer"
                        >
                          <Checkbox
                            checked={selectedCuisines.includes(cuisine)}
                            onCheckedChange={() => toggleCuisine(cuisine)}
                            data-testid={`cuisine-${cuisine.toLowerCase()}`}
                          />
                          <span className="text-sm">{cuisine}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Clear Filters */}
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setPriceRange([0, 500]);
                      setSelectedCuisines([]);
                      setGuestFilter(0);
                    }}
                    data-testid="clear-filters-btn"
                  >
                    Clear all filters
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            {/* Active filter badges */}
            {selectedCuisines.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {selectedCuisines.map((cuisine) => (
                  <span
                    key={cuisine}
                    className="px-3 py-1 bg-gray-100 rounded-full text-sm flex items-center gap-1 cursor-pointer hover:bg-gray-200"
                    onClick={() => toggleCuisine(cuisine)}
                  >
                    {cuisine}
                    <span className="ml-1">×</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
            <Button
              variant={!showMap ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setShowMap(false)}
              className={!showMap ? 'bg-white shadow-sm' : ''}
              data-testid="list-view-btn"
            >
              <List className="w-4 h-4 mr-2" />
              List
            </Button>
            <Button
              variant={showMap ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setShowMap(true)}
              className={showMap ? 'bg-white shadow-sm' : ''}
              data-testid="map-view-btn"
            >
              <Map className="w-4 h-4 mr-2" />
              Map
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex">
        {/* Results */}
        <div className={`${showMap ? 'w-1/2' : 'w-full'} p-6 overflow-y-auto`}>
          <h2 className="text-xl font-semibold mb-1" data-testid="results-title">
            {location ? `Experiences in "${location}"` : 'All experiences'}
          </h2>
          <p className="text-gray-500 mb-6" data-testid="results-count">
            {loading ? 'Loading...' : `${filteredExperiences.length} experiences found`}
            {hasDateFilter && !loading && (
              <span className="ml-1">
                • {selectedMonth ? getMonthName(parseInt(selectedMonth.split('-')[1]) - 1, parseInt(selectedMonth.split('-')[0])) : `${formatDisplayDate(startDate)}${endDate ? ` - ${formatDisplayDate(endDate)}` : ''}`}
              </span>
            )}
          </p>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#D65A31]" />
            </div>
          ) : filteredExperiences.length === 0 ? (
            <div className="text-center py-12" data-testid="no-results">
              <h3 className="text-lg font-semibold mb-2">No experiences found</h3>
              <p className="text-gray-500">Try adjusting your filters or search criteria</p>
              {hasDateFilter && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={clearDateFilter}
                >
                  Clear date filter
                </Button>
              )}
            </div>
          ) : (
            <div className={`grid gap-6 ${showMap ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'}`}>
              {filteredExperiences.map((experience) => (
                <ExperienceCard key={experience.id} experience={experience} />
              ))}
            </div>
          )}
        </div>

        {/* Map */}
        {showMap && (
          <div className="w-1/2 sticky top-36 h-[calc(100vh-144px)] bg-gray-100" data-testid="map-container">
            <div className="w-full h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <Map className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Map view</p>
                <p className="text-sm">{filteredExperiences.length} locations</p>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default SearchPage;
